import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  sendVerificationEmail,
  initiatePasswordReset,
  changeEmailAddress,
  isEmailVerified,
  reauthenticateUser,
} from '../../services/authService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { appTypographySx } from '../../theme';
import { SERIF, SANS, todayTokens } from '../../theme/todayTokens';
import { recordStyles } from '../property/PropertyRecord';
import { StatusPill, useHeroButtons } from '../ui/TodayPrimitives';
import New26Dialog, { DialogLabel, DialogTextField } from '../ui/New26Dialog';
import {
  Mail as MailIcon,
  Shield,
  CheckCircle,
  Lock,
} from 'lucide-react';
import {
  Box,
  Stack,
  Alert,
  Skeleton,
  ButtonBase,
} from '@mui/material';

const LoadingSkeleton = ({ s }) => {
  const pulse = { bgcolor: s.t.line, opacity: 0.5, borderRadius: '6px' };
  return (
    <Box sx={{ ...s.card, overflow: 'hidden' }}>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: { xs: 2.5, md: 3 },
            py: 2.5,
            borderTop: i > 1 ? `1px solid ${s.t.lineSoft}` : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', flexShrink: 0, ...pulse }} />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Skeleton height={18} width="40%" sx={{ bgcolor: s.t.line, opacity: 0.5 }} />
              <Skeleton height={14} width="55%" sx={{ bgcolor: s.t.line, opacity: 0.5 }} />
            </Box>
          </Box>
          <Box sx={{ width: 110, height: 40, borderRadius: '10px', flexShrink: 0, ...pulse }} />
        </Box>
      ))}
    </Box>
  );
};

const SecurityActionRow = ({ icon: Icon, iconColor, title, description, action, s, isLast }) => {
  const { t } = s;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        px: { xs: 2.5, md: 3 },
        py: 2.5,
        borderTop: isLast ? 0 : `1px solid ${t.lineSoft}`,
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: t.surfaceAlt },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: t.surfaceAlt,
            border: `1px solid ${t.lineSoft}`,
            color: iconColor,
          }}
        >
          <Icon size={18} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Box component="h3" sx={{ m: 0, fontFamily: SANS, fontSize: 15, fontWeight: 600, color: t.ink }}>
            {title}
          </Box>
          <Box component="p" sx={{ m: 0, mt: 0.25, fontSize: 13, color: t.muted, lineHeight: 1.5 }}>
            {description}
          </Box>
        </Box>
      </Stack>
      <Box sx={{ flexShrink: 0 }}>{action}</Box>
    </Box>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AccountManagement component error:', error, errorInfo);
    this.setState({ error });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback error={this.state.error} />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({ error }) {
  const { isDarkMode } = useTheme();
  const t = todayTokens(isDarkMode);
  const s = recordStyles(isDarkMode);
  const { heroBtn } = useHeroButtons();

  return (
    <Box className="account-management-page" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, p: 0 }}>
      <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box sx={{ ...s.eyebrow, fontSize: 12 }}>Account</Box>
        <Box component="h1" sx={{ m: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 0.95, letterSpacing: '-0.02em', color: t.ink }}>
          Account Management
        </Box>
      </Box>
      <Alert severity="error" sx={{ borderRadius: '14px', border: `1px solid ${t.line}` }}>
        {error?.toString() || 'Something went wrong loading this page.'}
      </Alert>
      <Box>
        <ButtonBase onClick={() => window.location.reload()} sx={heroBtn}>
          Try again
        </ButtonBase>
      </Box>
    </Box>
  );
}

const AccountManagement = () => {
  const { currentUser, refreshUser } = useAuth();
  const { isDarkMode } = useTheme();
  const t = todayTokens(isDarkMode);
  const s = recordStyles(isDarkMode);
  const { heroBtn } = useHeroButtons();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isEmailChangeDialogOpen, setIsEmailChangeDialogOpen] = useState(false);
  const [functionsAvailable, setFunctionsAvailable] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const navigate = useNavigate();

  const smallOutlineBtn = { ...s.outlineBtn, minHeight: 40, px: 1.75, fontSize: 13 };
  const smallNavyBtn = {
    ...smallOutlineBtn,
    bgcolor: t.navy,
    color: isDarkMode ? t.panelText : '#fff',
    border: 0,
    '&:hover': { bgcolor: t.navy, opacity: 0.92 },
  };

  const pageShellSx = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 3.5,
    ...appTypographySx(isDarkMode),
  };

  const emailVerified = isEmailVerified();

  useEffect(() => {
    const checkFunctions = async () => {
      try {
        setInitialLoading(true);

        if (!functions) {
          setFunctionsAvailable(false);
          setMessage({
            type: 'error',
            text: 'Firebase Functions could not be initialized. Please try again later.',
          });
          return;
        }

        try {
          httpsCallable(functions, 'sendPasswordReset');
          setFunctionsAvailable(true);
        } catch (callableError) {
          console.error('Firebase Functions callable creation failed:', callableError);
          setFunctionsAvailable(false);
          setMessage({
            type: 'error',
            text: 'Email functions are currently unavailable. Please try again later.',
          });
        }
      } catch (error) {
        console.error('Firebase Functions check failed:', error);
        setFunctionsAvailable(false);
        setMessage({
          type: 'error',
          text: 'Email functions are currently unavailable due to a system error. Please try again later.',
        });
      } finally {
        setInitialLoading(false);
      }
    };

    checkFunctions();
  }, []);

  const handleSendVerificationEmail = async () => {
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await sendVerificationEmail();
      setMessage({
        type: 'success',
        text: 'Verification email sent! Check your inbox and spam folder.',
      });
    } catch (error) {
      console.error('Error sending verification email:', error);
      setMessage({
        type: 'error',
        text: `Failed to send verification email: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.',
      });
      return;
    }

    if (!currentUser?.email) {
      setMessage({
        type: 'error',
        text: 'No email associated with your account.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await initiatePasswordReset(currentUser.email);
      setMessage({
        type: 'success',
        text: 'Password reset email sent! Check your inbox and spam folder.',
      });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setMessage({
        type: 'error',
        text: `Failed to send password reset email: ${error.message || 'Unknown error'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e?.preventDefault?.();

    if (!functionsAvailable) {
      setMessage({
        type: 'error',
        text: 'Email functions are currently unavailable. Please try again later.',
      });
      return;
    }

    if (!newEmail || !currentPassword) {
      setMessage({
        type: 'error',
        text: 'Please fill in all fields.',
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await reauthenticateUser(currentPassword);
      await changeEmailAddress(newEmail);

      setMessage({
        type: 'success',
        text: 'Email change initiated! Check your new email for verification.',
      });

      setNewEmail('');
      setCurrentPassword('');
      setIsEmailChangeDialogOpen(false);
      await refreshUser();
    } catch (error) {
      console.error('Error changing email:', error);
      if (error.code === 'auth/wrong-password') {
        setMessage({ type: 'error', text: 'Current password is incorrect.' });
      } else if (error.code === 'auth/email-already-in-use') {
        setMessage({ type: 'error', text: 'This email is already in use by another account.' });
      } else if (error.code === 'auth/invalid-email') {
        setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      } else {
        setMessage({
          type: 'error',
          text: `Failed to initiate email change: ${error.message || 'Unknown error'}`,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const closeEmailDialog = () => {
    setIsEmailChangeDialogOpen(false);
    setNewEmail('');
    setCurrentPassword('');
  };

  if (initialLoading) {
    return (
      <Box className="account-management-page" sx={pageShellSx}>
        <Box>
          <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={280} height={56} />
          <Skeleton variant="text" width={360} height={24} sx={{ mt: 1 }} />
        </Box>
        <LoadingSkeleton s={s} />
      </Box>
    );
  }

  return (
    <Box className="account-management-page" sx={pageShellSx}>
      {/* ── Header ── */}
      <Box
        component="section"
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
          <Box sx={{ ...s.eyebrow, fontSize: 12 }}>Account</Box>
          <Box component="h1" sx={{ m: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 0.95, letterSpacing: '-0.02em', color: t.ink }}>
            Account Management
          </Box>
          <Box component="p" sx={{ m: 0, fontSize: 14, color: t.body, lineHeight: 1.55, maxWidth: 560 }}>
            Manage your account security and email settings
            {currentUser?.email ? ` for ${currentUser.email}` : ''}
          </Box>
        </Box>
        <ButtonBase onClick={() => navigate('/profile')} sx={{ ...heroBtn, flexShrink: 0 }}>
          Back to profile
        </ButtonBase>
      </Box>

      {message.text && (
        <Alert
          severity={message.type === 'success' ? 'success' : 'error'}
          onClose={() => setMessage({ type: '', text: '' })}
          sx={{ borderRadius: '14px', border: `1px solid ${t.line}` }}
        >
          {message.text}
        </Alert>
      )}

      {!functionsAvailable && (
        <Alert severity="warning" sx={{ borderRadius: '14px', border: `1px solid ${t.line}` }}>
          Email functions are currently unavailable. Some features may not work properly.
        </Alert>
      )}

      {/* ── Account Security ── */}
      <Box sx={{ ...s.card, overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2.5, md: 3 },
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${t.lineSoft}`,
            bgcolor: t.surfaceAlt,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: t.surface,
              border: `1px solid ${t.lineSoft}`,
              color: t.navy,
              flexShrink: 0,
            }}
          >
            <Shield size={19} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box component="h2" sx={{ m: 0, fontFamily: SANS, fontSize: 16, fontWeight: 600, color: t.ink }}>
              Account Security
            </Box>
            <Box component="p" sx={{ m: 0, mt: 0.25, fontSize: 13, color: t.muted }}>
              Manage your email verification and password settings
            </Box>
          </Box>
        </Box>

        <SecurityActionRow
          icon={emailVerified ? CheckCircle : MailIcon}
          iconColor={emailVerified ? (isDarkMode ? t.green : '#1F4D38') : (isDarkMode ? t.amberText : '#6E4700')}
          title="Email Verification"
          description={
            emailVerified
              ? 'Your email is verified'
              : 'Verify your email to access all features'
          }
          s={s}
          action={
            emailVerified ? (
              <StatusPill tone="green" label="Verified" />
            ) : (
              <ButtonBase
                onClick={handleSendVerificationEmail}
                disabled={loading || !functionsAvailable}
                sx={smallNavyBtn}
              >
                {loading ? 'Sending…' : 'Verify email'}
              </ButtonBase>
            )
          }
        />

        <SecurityActionRow
          icon={MailIcon}
          iconColor={t.navy}
          title="Password Reset"
          description="Reset your password if you've forgotten it or want to change it"
          s={s}
          action={
            <ButtonBase
              onClick={handlePasswordReset}
              disabled={loading || !functionsAvailable}
              sx={smallOutlineBtn}
            >
              {loading ? 'Sending…' : 'Reset password'}
            </ButtonBase>
          }
        />

        <SecurityActionRow
          icon={MailIcon}
          iconColor={t.navy}
          title="Change Email"
          description="Update your email address. You'll need to verify the new email"
          s={s}
          isLast
          action={
            <ButtonBase
              onClick={() => setIsEmailChangeDialogOpen(true)}
              disabled={!functionsAvailable}
              sx={smallOutlineBtn}
            >
              Change email
            </ButtonBase>
          }
        />
      </Box>

      {/* ── Change email dialog ── */}
      <New26Dialog
        open={isEmailChangeDialogOpen}
        onClose={closeEmailDialog}
        title="Change email address"
        subtitle="Enter your new email address and current password to confirm the change."
        maxWidth="sm"
        secondaryAction={{ label: 'Cancel', onClick: closeEmailDialog }}
        primaryAction={{
          label: loading ? 'Processing…' : 'Confirm change',
          onClick: handleEmailChange,
          disabled: loading || !newEmail || !currentPassword || !functionsAvailable,
        }}
      >
        <Box component="form" onSubmit={handleEmailChange}>
          <DialogLabel htmlFor="am-new-email" sx={{ mt: 2.5 }}>New email address</DialogLabel>
          <DialogTextField
            id="am-new-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <DialogLabel htmlFor="am-current-password">Current password</DialogLabel>
          <DialogTextField
            id="am-current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          {/* allow native form submission via Enter without rendering a visible submit button */}
          <button type="submit" aria-hidden="true" tabIndex={-1} style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none', border: 0, padding: 0 }} />
        </Box>
      </New26Dialog>
    </Box>
  );
};

const AccountManagementWithErrorBoundary = () => (
  <ErrorBoundary>
    <AccountManagement />
  </ErrorBoundary>
);

export default AccountManagementWithErrorBoundary;
